<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card p-fluid followup-settings">
        <h4>Follow-up Emails</h4>
        <p class="hint">
          How and when quotes are chased by email. Changes apply to the next send.
        </p>

        <div v-if="loading" class="p-text-center"><ProgressSpinner /></div>

        <div v-else-if="loadError" class="p-invalid">{{ loadError }}</div>

        <div v-else>
          <div id="followupSummary" class="followup-summary p-mb-3">{{ summary }}</div>

          <TabView>
            <TabPanel :header="tabHeader('sending')" :active.sync="tabActive.sending">
              <p class="hint tab-explain">{{ tabExplain('sending') }}</p>
              <div class="p-field p-d-flex p-ai-center">
                <InputSwitch id="enabled" v-model="form.enabled" />
                <label for="enabled" class="p-ml-2 p-mb-0">Send follow-up emails</label>
              </div>
              <small class="hint">When off, nothing is sent and no quote is enrolled. When switched on, an email whose date has already passed is skipped: only emails due from then on go out.</small>
              <small id="switchedOnAt" class="hint p-d-block" v-if="saved && saved.enabled && saved.switchedOnAt">Switched on {{ saved.switchedOnAt }}.</small>
              <small class="p-invalid" v-if="errors.enabled">{{ errors.enabled }}</small>

              <div class="p-formgrid p-grid">
                <div class="p-field p-col">
                  <label for="workStart">Working day starts</label>
                  <InputMask id="workStart" v-model="form.workStart" mask="99:99" placeholder="09:00"
                    :class="{ 'p-invalid': errors.workStart }" />
                  <small class="p-invalid" v-if="errors.workStart">{{ errors.workStart }}</small>
                </div>
                <div class="p-field p-col">
                  <label for="workEnd">Working day ends</label>
                  <InputMask id="workEnd" v-model="form.workEnd" mask="99:99" placeholder="17:00"
                    :class="{ 'p-invalid': errors.workEnd }" />
                  <small class="p-invalid" v-if="errors.workEnd">{{ errors.workEnd }}</small>
                </div>
              </div>
              <small class="hint">UK time. An email due outside these hours waits for the next working day.</small>

              <div class="p-field">
                <label>Working days</label>
                <div class="p-d-flex p-flex-wrap">
                  <div v-for="d in weekDays" :key="d.value" class="p-field-checkbox p-mr-3 p-mb-0">
                    <Checkbox :id="'workDay' + d.value" name="workDays" :value="d.value" v-model="form.workDays" />
                    <label :for="'workDay' + d.value">{{ d.label }}</label>
                  </div>
                </div>
                <small class="hint">Bank holidays are skipped as well.</small>
                <small class="p-invalid" v-if="errors.workDays">{{ errors.workDays }}</small>
              </div>

              <div class="p-field">
                <label for="dailyCap">Daily limit</label>
                <InputNumber id="dailyCap" v-model="form.dailyCap" :min="0" :useGrouping="false"
                  :class="{ 'p-invalid': errors.dailyCap }" />
                <small class="hint">The most follow-up emails sent in one day. The rest go the next working day.</small>
                <small class="p-invalid" v-if="errors.dailyCap">{{ errors.dailyCap }}</small>
              </div>

              <h5>Email addresses</h5>
              <div class="p-field">
                <label for="internalRecipientOverride">Send all follow-up emails to (testing only)</label>
                <InputText id="internalRecipientOverride" v-model="form.internalRecipientOverride"
                  :class="{ 'p-invalid': errors.internalRecipientOverride }" />
                <small class="hint">One email address. When set, no customer receives anything; every email goes here. Leave blank in normal use.</small>
                <small class="p-invalid" v-if="errors.internalRecipientOverride">{{ errors.internalRecipientOverride }}</small>
              </div>

              <div class="p-field">
                <label for="tenantDomains">Company email domains for Outlook</label>
                <InputText id="tenantDomains" v-model="form.tenantDomains" placeholder="e.g. mbp.co.uk"
                  :class="{ 'p-invalid': errors.tenantDomains }" />
                <small class="hint">Separate with commas. Leave blank for no check.</small>
                <small class="p-invalid" v-if="errors.tenantDomains">{{ errors.tenantDomains }}</small>
              </div>
            </TabPanel>
            <TabPanel :header="tabHeader('chased')" :active.sync="tabActive.chased">
              <p class="hint tab-explain">{{ tabExplain('chased') }}</p>
              <div class="p-field">
                <label for="excludedProjectTypeIds">Don't chase these project types</label>
                <MultiSelect id="excludedProjectTypeIds" v-model="form.excludedProjectTypeIds" :options="projectTypes"
                  optionLabel="projTypeName" optionValue="id" placeholder="None"
                  :class="{ 'p-invalid': errors.excludedProjectTypeIds }" />
                <small class="hint">Quotes of these project types are never enrolled (for example tenders).</small>
                <small class="p-invalid" v-if="errors.excludedProjectTypeIds">{{ errors.excludedProjectTypeIds }}</small>
              </div>
              <div class="p-field">
                <label for="eligibleQuoteStatuses">Chase quotes with these statuses</label>
                <MultiSelect id="eligibleQuoteStatuses" v-model="form.eligibleQuoteStatuses" :options="quoteStatuses"
                  optionLabel="statusName" optionValue="statusName" placeholder="Choose"
                  :class="{ 'p-invalid': errors.eligibleQuoteStatuses }" />
                <small class="hint">A quote that moves to any other status stops being chased.</small>
                <small class="p-invalid" v-if="errors.eligibleQuoteStatuses">{{ errors.eligibleQuoteStatuses }}</small>
              </div>

              <div class="p-field-checkbox">
                <Checkbox id="skipExpiredQuotes" v-model="form.skipExpiredQuotes" :binary="true" />
                <label for="skipExpiredQuotes">Don't chase expired quotes</label>
              </div>
              <small class="hint">Quotes past their expiry date are left alone.</small>
              <small class="p-invalid" v-if="errors.skipExpiredQuotes">{{ errors.skipExpiredQuotes }}</small>
            </TabPanel>
            <TabPanel :header="tabHeader('approval')" :active.sync="tabActive.approval">
              <p class="hint tab-explain">{{ tabExplain('approval') }}</p>
              <div class="p-field-checkbox">
                <Checkbox id="firstSendNeedsApproval" v-model="form.firstSendNeedsApproval" :binary="true" />
                <label for="firstSendNeedsApproval">First email needs approval</label>
              </div>
              <small class="hint">The salesperson approves each quote on its Follow-up tab before the first email goes.</small>
              <small class="p-invalid" v-if="errors.firstSendNeedsApproval">{{ errors.firstSendNeedsApproval }}</small>

              <div class="p-field-checkbox p-mt-2">
                <Checkbox id="manualEnrolmentNeedsApproval" v-model="form.manualEnrolmentNeedsApproval" :binary="true" />
                <label for="manualEnrolmentNeedsApproval">Hand-enrolled quotes need approval</label>
              </div>
              <small class="hint">Applies to quotes enrolled from the Enrol Quotes screen or the Follow-up tab.</small>
              <small class="p-invalid" v-if="errors.manualEnrolmentNeedsApproval">{{ errors.manualEnrolmentNeedsApproval }}</small>
            </TabPanel>
            <TabPanel :header="tabHeader('escalation')" :active.sync="tabActive.escalation">
              <p class="hint tab-explain">{{ tabExplain('escalation') }}</p>
              <div class="p-formgrid p-grid">
                <div class="p-field p-col">
                  <label for="silenceEscalationDays">Days of silence before escalating</label>
                  <InputNumber id="silenceEscalationDays" v-model="form.silenceEscalationDays" :min="1" :useGrouping="false"
                    :class="{ 'p-invalid': errors.silenceEscalationDays }" />
                  <small class="hint">Counted from the last step sent.</small>
                  <small class="p-invalid" v-if="errors.silenceEscalationDays">{{ errors.silenceEscalationDays }}</small>
                </div>
                <div class="p-field p-col">
                  <label for="escalationRecipient">Escalation goes to</label>
                  <Dropdown id="escalationRecipient" v-model="form.escalationRecipient" :options="escalationRecipients"
                    optionLabel="label" optionValue="value" placeholder="Choose"
                    :class="{ 'p-invalid': errors.escalationRecipient }" />
                  <small class="p-invalid" v-if="errors.escalationRecipient">{{ errors.escalationRecipient }}</small>
                </div>
              </div>
              <div class="p-formgrid p-grid">
                <div class="p-field p-col">
                  <label for="chaseTaskDueWorkingDays">Chase task due after (working days)</label>
                  <InputNumber id="chaseTaskDueWorkingDays" v-model="form.chaseTaskDueWorkingDays" :min="0" :useGrouping="false"
                    :class="{ 'p-invalid': errors.chaseTaskDueWorkingDays }" />
                  <small class="hint">At no reply the salesperson always gets a chase Call task, with one reminder email at once. It is due this many working days later, at the start of the working day.</small>
                  <small class="p-invalid" v-if="errors.chaseTaskDueWorkingDays">{{ errors.chaseTaskDueWorkingDays }}</small>
                </div>
              </div>
              <div class="p-field-checkbox">
                <Checkbox id="escalationEmailsSalesperson" v-model="form.escalationEmailsSalesperson" :binary="true" />
                <label for="escalationEmailsSalesperson">Also email the salesperson "No reply on quote"</label>
              </div>
              <small class="hint">Off (recommended): the chase task and its reminder are the salesperson's only alert, so they get one email, not two.</small>
              <small class="p-invalid" v-if="errors.escalationEmailsSalesperson">{{ errors.escalationEmailsSalesperson }}</small>
              <template v-if="emailsSalesperson">
              <div class="p-field">
                <label for="escalationSubjectTemplate">Escalation subject to the salesperson</label>
                <InputText id="escalationSubjectTemplate" v-model="form.escalationSubjectTemplate"
                  :class="{ 'p-invalid': errors.escalationSubjectTemplate }" />
                <small class="p-invalid" v-if="errors.escalationSubjectTemplate">{{ errors.escalationSubjectTemplate }}</small>
              </div>
              <div class="p-field">
                <label for="escalationBodyTemplate">Escalation message to the salesperson</label>
                <Textarea id="escalationBodyTemplate" v-model="form.escalationBodyTemplate" :autoResize="true" rows="5"
                  :class="{ 'p-invalid': errors.escalationBodyTemplate }" />
                <small class="hint">You can use {{ mergeFieldsHint }}.</small>
                <small class="p-invalid" v-if="errors.escalationBodyTemplate">{{ errors.escalationBodyTemplate }}</small>
              </div>
              </template>
              <template v-if="escalatesToCustomer">
                <div class="p-field">
                  <label for="customerEscalationSubjectTemplate">Escalation subject to the customer</label>
                  <InputText id="customerEscalationSubjectTemplate" v-model="form.customerEscalationSubjectTemplate"
                    :class="{ 'p-invalid': errors.customerEscalationSubjectTemplate }" />
                  <small class="p-invalid" v-if="errors.customerEscalationSubjectTemplate">{{ errors.customerEscalationSubjectTemplate }}</small>
                </div>
                <div class="p-field">
                  <label for="customerEscalationBodyTemplate">Escalation message to the customer</label>
                  <Textarea id="customerEscalationBodyTemplate" v-model="form.customerEscalationBodyTemplate" :autoResize="true" rows="5"
                    :class="{ 'p-invalid': errors.customerEscalationBodyTemplate }" />
                  <small class="hint">You can use {{ mergeFieldsHint }}.</small>
                  <small class="p-invalid" v-if="errors.customerEscalationBodyTemplate">{{ errors.customerEscalationBodyTemplate }}</small>
                </div>
              </template>
            </TabPanel>
            <TabPanel :header="tabHeader('wording')" :active.sync="tabActive.wording">
              <p class="hint tab-explain">{{ tabExplain('wording') }}</p>
              <FollowupSteps :embedded="true" />
            </TabPanel>
            <TabPanel :header="tabHeader('holidays')" :active.sync="tabActive.holidays">
              <p class="hint tab-explain">{{ tabExplain('holidays') }}</p>
              <BankHolidays :embedded="true" />
            </TabPanel>
          </TabView>

          <div v-if="settingsTabShown" class="p-d-flex p-jc-end p-mt-3">
            <Button id="runFollowupNow" label="Run now" icon="pi pi-play" class="p-button-secondary p-mr-auto"
              style="width: auto" :disabled="running" @click="askRunNow" />
            <Button label="Reset" icon="pi pi-refresh" class="p-button-text p-mr-2" style="width: auto" @click="reset" />
            <Button id="saveFollowupSettings" label="Save" icon="pi pi-check" style="width: auto"
              :disabled="saving" @click="save" />
          </div>
        </div>

        </div>

        <Dialog :visible.sync="runNowDialog" header="Run follow-ups now" :modal="true" :style="{ width: '420px' }">
          <p>Run one follow-up pass now? Emails go only where the send setting allows.</p>
          <template #footer>
            <Button label="No" icon="pi pi-times" class="p-button-text" @click="runNowDialog = false" />
            <Button id="confirmRunNow" label="Yes, run now" icon="pi pi-check" class="p-button-text"
              :disabled="running" @click="runNow" />
          </template>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<script>
import FollowupService, { errorMessage, fieldErrors } from '@/service/FollowupService'
import OpportunityService from '@/service/OpportunityService'
import QuotationService from '@/service/QuotationService'
import {
  toForm, toPayload, runNowSummary, settingsSummary, tabWithError, SETTINGS_TABS, WEEK_DAYS, ESCALATION_RECIPIENTS
} from './followupSettingsForm'
import FollowupSteps from './followupSteps/followupSteps.vue'
import BankHolidays from './bankHolidays/bankHolidays.vue'

function tabsActive(key) {
  return SETTINGS_TABS.reduce((out, t) => { out[t.key] = t.key === key; return out }, {})
}

export default {
  components: { FollowupSteps, BankHolidays },
  data: () => ({
    followupService: new FollowupService(),
    opportunityService: new OpportunityService(),
    quotationService: new QuotationService(),
    saved: null,
    form: toForm(null),
    errors: {},
    projectTypes: [],
    quoteStatuses: [],
    loading: true,
    loadError: '',
    saving: false,
    runNowDialog: false,
    running: false,
    weekDays: WEEK_DAYS,
    escalationRecipients: ESCALATION_RECIPIENTS,
    steps: [],
    tabActive: tabsActive('sending'),
    mergeFieldsHint: '{{contactFirstName}}, {{quoteRef}}, {{product}}, {{project}}, {{company}}, {{salespersonName}} and {{signature}}'
  }),
  computed: {
    summary() {
      return settingsSummary(this.saved, this.steps)
    },
    // Save / Reset / Run now belong to the settings tabs; the steps and bank holidays tabs save themselves
    settingsTabShown() {
      return !this.tabActive.wording && !this.tabActive.holidays
    },
    // the customer templates are used only when the escalation goes to the customer
    /** The salesperson's "No reply" email goes only when ticked, and only to SALESPERSON or BOTH (option C). */
    emailsSalesperson() {
      return !!this.form.escalationEmailsSalesperson && this.form.escalationRecipient !== 'CUSTOMER'
    },
    escalatesToCustomer() {
      return this.form.escalationRecipient === 'CUSTOMER' || this.form.escalationRecipient === 'BOTH'
    }
  },
  created() {
    this.load()
  },
  methods: {
    tabHeader(key) {
      var t = SETTINGS_TABS.find(x => x.key === key)
      var keys = Object.keys(this.errors || {})
      return t.header + (t.fields.some(f => keys.indexOf(f) > -1) ? ' (!)' : '')
    },
    tabExplain(key) {
      return SETTINGS_TABS.find(x => x.key === key).explain
    },
    showTab(key) {
      this.tabActive = tabsActive(key)
    },
    loadSteps() {
      return this.followupService.getSteps()
        .then(data => { this.steps = data || [] })
        .catch(() => { this.steps = [] })
    },
    load() {
      this.loading = true
      this.loadSteps()
      this.opportunityService.getAllActiveProjectTypes()
        .then(data => { this.projectTypes = data || [] })
        .catch(() => { this.projectTypes = [] })
      this.quotationService.getAllActiveQuotationStatuses()
        .then(data => { this.quoteStatuses = data || [] })
        .catch(() => { this.quoteStatuses = [] })
      return this.followupService.getSettings()
        .then(data => {
          this.saved = data
          this.form = toForm(data)
          this.loadError = ''
        })
        .catch(err => {
          this.loadError = errorMessage(err, 'The follow-up settings could not be loaded.')
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: this.loadError, life: 5000 })
        })
        .finally(() => { this.loading = false })
    },
    askRunNow() {
      this.runNowDialog = true
    },
    runNow() {
      this.runNowDialog = false
      this.running = true
      return this.followupService.runNow()
        .then(result => {
          this.$toast.add({ severity: 'success', summary: 'Follow-up pass finished', detail: runNowSummary(result), life: 8000 })
        })
        .catch(err => {
          // 409: a pass is already running, or the user may not do this
          this.$toast.add({
            severity: 'error',
            summary: 'Not run',
            detail: errorMessage(err, 'The follow-up pass could not be run.'),
            life: 5000
          })
        })
        .finally(() => { this.running = false })
    },
    reset() {
      this.errors = {}
      this.form = toForm(this.saved)
    },
    save() {
      this.errors = {}
      this.saving = true
      return this.followupService.saveSettings(toPayload(this.form))
        .then(data => {
          this.saved = data
          this.form = toForm(data)
          this.$toast.add({
            severity: 'success',
            summary: 'Follow-up settings saved',
            detail: 'The follow-up settings were saved.',
            life: 5000
          })
        })
        .catch(err => {
          // a 400 may carry field messages (shown inline) and a top-level error (e.g. unknown keys);
          // a 409 carries only the top-level error. The toast always shows the top-level one when present.
          this.errors = fieldErrors(err)
          var withError = tabWithError(this.errors)
          if (withError) this.showTab(withError)
          this.$toast.add({
            severity: 'error',
            summary: 'Not saved',
            detail: errorMessage(err, 'The follow-up settings could not be saved.'),
            life: 5000
          })
        })
        .finally(() => { this.saving = false })
    }
  }
}
</script>

<style scoped>
.followup-settings { max-width: 760px; }
.followup-settings h5 { margin-top: 1.5rem; }
.followup-settings .followup-summary { padding: 0.5rem 0.75rem; background: #f1f5f9; border-radius: 4px; font-weight: 600; }
.followup-settings .tab-explain { margin-top: 0; }
.followup-settings .hint { display: block; color: #6c757d; margin-bottom: 0.5rem; }
.followup-settings small.p-invalid { display: block; color: #d32f2f; }
</style>
